import React, { useState } from "react";
import {
  ChevronRight,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils/formatDate";

const STATUS_CLASSES = {
  "Payment Pending": "text-cfWarningText bg-cfWarningBg border-cfWarningBorder",
  "Approval Pending": "text-cfPrimary bg-cfDisabledLight border-cfDisabled",
  Approved: "text-cfActiveText bg-cfActiveBg border-cfActiveBorder",
  Rejected: "text-cfIssuedCredentialTemplateCardStatusFailedBG bg-red-200 border-red-300",
  Transferred: "text-amber-600 bg-amber-50 border-amber-200",
};
const ORG_FALLBACK_STATUS_CLASSES = "text-cfPrimary bg-cfDisabledLight border-cfDisabled";

const EVENT_CONFIG = {
  CREATED: "auditLogs:event_created",
  PAYMENT_INITIATED: "auditLogs:event_payment_initiated",
  PAYMENT_SUCCEEDED: "auditLogs:event_payment_succeeded",
  TRANSFERRED: "auditLogs:event_transferred",
  APPROVED: "auditLogs:event_approved",
  REJECTED: "auditLogs:event_rejected",
};

const getEventSentiment = (eventType) => {
  const key = String(eventType || "").toUpperCase();
  if (
    key.includes("CREATED") ||
    key.includes("INITIATED") ||
    key.includes("SUCCEEDED") ||
    key.includes("APPROVED") ||
    key.includes("COMPLETED") ||
    key.includes("SUCCESS")
  )
    return "positive";
  if (
    key.includes("REJECTED") ||
    key.includes("FAILED") ||
    key.includes("CANCELLED") ||
    key.includes("CANCELED") ||
    key.includes("REVOKED") ||
    key.includes("EXPIRED")
  )
    return "negative";
  if (key.includes("TRANSFERRED") || key.includes("PENDING"))
    return "neutral";
  return "default";
};

const SENTIMENT_CIRCLE = {
  positive: "bg-cfActiveBg border-2 border-cfActiveBorder",
  negative: "bg-red-200 border-2 border-red-300",
  neutral: "bg-cfWarningBg border-2 border-cfWarningBorder",
  default: "bg-cfDisabledLight border-2 border-cfDisabled",
};

const METADATA_LABELS = {
  org_code: "auditLogs:meta_org_code",
  has_document: "auditLogs:meta_has_document",
  verification_type: "auditLogs:meta_verification_type",
  is_payment_required: "auditLogs:meta_is_payment_required",
  is_legacy_admin_approval_required: "auditLogs:meta_is_legacy_admin_approval_required",
  comments: "auditLogs:meta_comments",
  order_id: "auditLogs:meta_order_id",
  verification_cost: "auditLogs:meta_verification_cost",
  payment_id: "auditLogs:meta_payment_id",
  mode_of_payment: "auditLogs:meta_mode_of_payment",
  issuer_changed: "auditLogs:meta_issuer_changed",
  new_issuer_code: "auditLogs:meta_new_issuer_code",
  new_issuer_name: "auditLogs:meta_new_issuer_name",
  programme_changed: "auditLogs:meta_programme_changed",
  new_programme_code: "auditLogs:meta_new_programme_code",
  new_programme_name: "auditLogs:meta_new_programme_name",
  previous_issuer_code: "auditLogs:meta_previous_issuer_code",
  previous_issuer_name: "auditLogs:meta_previous_issuer_name",
  previous_programme_code: "auditLogs:meta_previous_programme_code",
  previous_programme_name: "auditLogs:meta_previous_programme_name",
  issuer_comments: "auditLogs:meta_issuer_comments",
};

const formatKeyLabel = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const toEventCardKey = (event, eventIndex = 0) => {
  const raw =
    event?.id ??
    `${event?.event ?? "event"}-${event?.created_at ?? "unknown"}-${eventIndex}`;
  return String(raw)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || `unknown-${eventIndex}`;
};

const formatMetaValue = (key, value, t) => {
  if (typeof value === "boolean") return value ? t("auditLogs:yes") : t("auditLogs:no");
  if (key === "verification_cost") {
    const numericValue = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(numericValue)) {
      return `₹ ${(numericValue / 100).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
  }
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const classes = STATUS_CLASSES[status] ?? ORG_FALLBACK_STATUS_CLASSES;
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${classes}`}>
      {status}
    </span>
  );
};

StatusBadge.propTypes = { status: PropTypes.string };
StatusBadge.defaultProps = { status: null };

const StatusTransition = ({ from, to }) => {
  if (!from && !to) return null;
  if (!from) return <StatusBadge status={to} />;
  if (!to) return <StatusBadge status={from} />;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <StatusBadge status={from} />
      <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      <StatusBadge status={to} />
    </div>
  );
};

StatusTransition.propTypes = { from: PropTypes.string, to: PropTypes.string };
StatusTransition.defaultProps = { from: null, to: null };

const MetadataPanel = ({ metadata, panelId }) => {
  const { t } = useTranslation();
  if (!metadata) return null;
  const entries = Object.entries(metadata).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  if (entries.length === 0) return null;

  const isScrollable = entries.length > 10;

  return (
    <div
      id={panelId}
      className={`mt-3 ${isScrollable ? "max-h-64 overflow-y-auto pr-1" : ""}`}
    >
      {entries.map(([key, value]) => (
        <div key={key} className="flex justify-between items-center py-2 text-xs">
          <span className="font-medium uppercase tracking-wide shrink-0 mr-4 text-gray-500">
            {METADATA_LABELS[key] ? t(METADATA_LABELS[key]) : formatKeyLabel(key)}
          </span>
          <span className="font-semibold text-right break-all text-gray-900">
            {formatMetaValue(key, value, t)}
          </span>
        </div>
      ))}
    </div>
  );
};

MetadataPanel.propTypes = {
  panelId: PropTypes.string,
  metadata: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};
MetadataPanel.defaultProps = { metadata: null };

const EventCard = ({ event, isLast, eventIndex = 0 }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const eventKey = toEventCardKey(event, eventIndex);
  const labelKey = EVENT_CONFIG[event.event];
  const label = labelKey ? t(labelKey) : event.event;
  const sentiment = getEventSentiment(event.event);
  const circleClass = SENTIMENT_CIRCLE[sentiment];

  const hasMetadata =
    event.metadata &&
    Object.keys(event.metadata).some(
      (k) =>
        event.metadata[k] !== null &&
        event.metadata[k] !== undefined &&
        event.metadata[k] !== "",
    );

  return (
    <div id={`audit-logs-event-card-${eventKey}`} className="flex gap-4">
      <div className="flex flex-col items-center">
        <div
          id={`audit-logs-event-card-indicator-${eventKey}`}
          className={`w-5 h-5 rounded-full shrink-0 mt-3 shadow-sm ${circleClass}`}
        />
        {!isLast && (
          <div className="w-px flex-1 mt-1 bg-gray-200" style={{ minHeight: "1.5rem" }} />
        )}
      </div>

      <div className="flex-1 pb-5">
        <div
          id={`audit-logs-event-card-body-${eventKey}`}
          className="bg-white rounded-2xl border border-cfCardBorder overflow-hidden shadow-sm"
        >
          <div className="h-[3px] w-full" />

          <div className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-cfHeadingText">{label}</h3>

                <div className="flex items-center gap-1.5 mt-1.5">
                  <Clock className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                  <span
                    id={`audit-logs-event-card-date-${eventKey}`}
                    className="text-xs text-gray-500"
                  >
                    {formatDate(event.created_at)}
                  </span>
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
                  {event.verifier_name && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500">
                        {t("auditLogs:verifier")}:{" "}
                        <span className="font-semibold text-gray-900">
                          {event.verifier_name}
                        </span>
                      </span>
                    </div>
                  )}
                  {event.user_name && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500">
                        {t("auditLogs:user")}:{" "}
                        <span className="font-semibold text-gray-900">
                          {event.user_name}
                        </span>
                      </span>
                    </div>
                  )}
                  {event.verifier_organization_name && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500">
                        {t("auditLogs:organisation")}:{" "}
                        <span className="font-semibold text-gray-900">
                          {event.verifier_organization_name}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="shrink-0 mt-0.5">
                <StatusTransition from={event.from_status} to={event.to_status} />
              </div>
            </div>

            {hasMetadata && (
              <>
                <button
                  type="button"
                  id={`audit-logs-event-card-toggle-details-${eventKey}`}
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-4 flex items-center gap-1 text-xs font-semibold text-cfPrimary focus:outline-none"
                >
                  {expanded ? (
                    <>
                      <ChevronUp className="w-3.5 h-3.5" />
                      {t("auditLogs:hide_details")}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3.5 h-3.5" />
                      {t("auditLogs:show_details")}
                    </>
                  )}
                </button>
                {expanded && (
                  <MetadataPanel
                    metadata={event.metadata}
                    panelId={`audit-logs-event-card-metadata-${eventKey}`}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

EventCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    event: PropTypes.string,
    created_at: PropTypes.string,
    from_status: PropTypes.string,
    to_status: PropTypes.string,
    verifier_name: PropTypes.string,
    user_name: PropTypes.string,
    verifier_organization_name: PropTypes.string,
    metadata: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    ),
  }).isRequired,
  isLast: PropTypes.bool.isRequired,
  eventIndex: PropTypes.number,
};

export default EventCard;
